<script setup lang="ts">
  import type { Agency } from '@gat-ship/types'
  import { computed, ref } from 'vue'
  import { testIds } from '@gat-ship/types'
  import {
    Button,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderIcon,
    ModalHeaderTitle,
    ModalSection,
    ModalSectionHeader,
    ModalSectionHeaderTitle,
    useModalStack
  } from '@gat-ship/vue-components'
  import { IconDeactivate, IconLoading } from '@gat-ship/icons'
  import { agencyKey } from '@gat-ship/types/injection-keys'
  import { inject } from 'vue'

  const agencyProvider = inject(agencyKey)

  const isOpen = ref(false)
  const isLoading = ref(false)
  const agency = ref<Agency | undefined>()
  const agencyName = computed(() => agency.value?.name)

  function closeDialog() {
    agency.value = undefined
    isLoading.value = false
    isOpen.value = false
  }

  const stack = useModalStack()

  export interface ShowDeactivateAgencyModalParams {
    agency: Agency
  }

  function showModal({
    agency: agencyParams
  }: ShowDeactivateAgencyModalParams) {
    isOpen.value = true
    agency.value = agencyParams
  }

  async function onDeactivateAgencyClick() {
    if (!agency.value) {
      throw new Error('No agency found')
    }
    const agencyId = agency.value.id
    isLoading.value = true
    try {
      await agencyProvider?.commands.deactivate(agencyId)
    } finally {
      closeDialog()
      // Close the parent agency detail modal after deactivation (was a route
      // nav back to the agency-structure list before central-modal-host).
      stack.close('agency')
    }
  }

  defineExpose({
    showModal
  })
</script>
<template>
  <modal v-model:open="isOpen" type="dialog">
    <ModalHeader>
      <ModalHeaderTitle>
        <ModalHeaderIcon>
          <IconDeactivate class="size-5" :stroke-width="1.5" />
        </ModalHeaderIcon>
        Deactivate Agency
      </ModalHeaderTitle>
      <ModalHeaderDescription v-if="agency">
        {{ agency.name }}
      </ModalHeaderDescription>
    </ModalHeader>
    <ModalContent>
      <ModalSection>
        <div class="flex flex-col gap-4 text-sm">
          <div>
            You're about to start the process of deactivating
            <span class="font-medium">{{ agencyName ?? 'X' }}</span
            >.
          </div>
          <ModalSectionHeader>
            <ModalSectionHeaderTitle
              >What else you should know</ModalSectionHeaderTitle
            >
          </ModalSectionHeader>
          <div>
            You can restore your agency if it was accidentally or wrongfully
            deactivated for up to 30 days after deactivation.
          </div>
          <div>
            This agency will still be visible in lists and other places where it
            has previously been used, but can not be assigned or used in the
            future after deactivation.
          </div>
        </div>
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <Button
        :data-testid="testIds.common.dialog.saveButton"
        :disabled="isLoading"
        @click="onDeactivateAgencyClick"
      >
        <template v-if="isLoading">
          <IconLoading class="size-4" />
          Deactivating...
        </template>
        <template v-else> Deactivate Agency </template>
      </Button>
      <Button v-if="!isLoading" variant="ghost" @click="isOpen = false">
        Cancel
      </Button>
    </ModalFooter>
  </modal>
</template>
